import type {ComboboxData} from '@mantine/core';
import type {IListItem, ISelectOption} from 'src/types/Form';

export function mapEnumToSelectOption<T extends object>(
  obj: T
): ISelectOption[] {
  return Object.entries(obj)
    .filter(e => isNaN(Number(e[0])))
    .filter(e => e[0] !== 'Invalid')
    .map(([label, value]) => ({
      id: String(value),
      label: label.splitUppercase(),
    }))
    .sort((a, b) => (a.label === b.label ? 0 : a.label > b.label ? 1 : -1));
}

export function mapEnumToListOption<T extends object>(
  obj: T,
  split: boolean = true
): IListItem[] {
  return Object.entries(obj)
    .filter(e => isNaN(Number(e[0])))
    .filter(e => e[0] !== 'Invalid')
    .map(([label, value]) => ({
      value: String(value),
      label: split ? label.splitUppercase() : label,
    }))
    .sort((a, b) => (a.label === b.label ? 0 : a.label > b.label ? 1 : -1));
}

export function mapEnumToComboboxData<T extends object>(
  obj: T,
  split: boolean = true
): ComboboxData {
  return Object.entries(obj)
    .filter(e => isNaN(Number(e[0])))
    .filter(e => e[0] !== 'Invalid')
    .map(([label, value]) => ({
      value: String(value),
      label: split ? label.splitUppercase() : label,
    }))
    .sort((a, b) => (a.label === b.label ? 0 : a.label > b.label ? 1 : -1));
}

export function mapEnumToValues<T extends object>(obj: T): string[] {
  return Object.entries(obj)
    .filter(e => isNaN(Number(e[0])))
    .filter(e => e[0] !== 'Invalid')
    .map(([, value]) => String(value));
}

export function mapEnumValueToName<T extends object>(
  obj: T,
  value?: number
): string | undefined {
  if (value === undefined) return undefined;
  const name = (obj as Record<number, unknown>)[value];
  return typeof name === 'string' ? name : undefined;
}

export function mapEnumNameToValue<T extends object>(
  obj: T,
  name?: string | null
): T[keyof T] | undefined {
  if (!name) return undefined;
  const value = (obj as Record<string, unknown>)[name];
  return typeof value === 'number' ? (value as T[keyof T]) : undefined;
}
